"use client";

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Handshake, 
  ShoppingBag, 
  ChevronDown, 
  Check 
} from 'lucide-react';

export default function RoleSwitcher() {
  const { user, activeRole, switchRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user || user.role !== 'fpo_connect') {
    return null;
  }

  const roles = [
    {
      id: 'fpo_connect',
      label: 'FPO Hub',
      description: 'Manage market details & FPO settings',
      icon: Handshake,
      colorClass: 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100/70',
      activeColorClass: 'bg-amber-600 text-white hover:bg-amber-700',
      badgeColor: 'bg-amber-100 text-amber-800'
    },
    {
      id: 'buyer',
      label: 'Buyer Portal',
      description: 'Browse products & submit enquiries',
      icon: ShoppingBag,
      colorClass: 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100/70',
      activeColorClass: 'bg-emerald-600 text-white hover:bg-emerald-700',
      badgeColor: 'bg-emerald-100 text-emerald-800'
    }
  ];

  const currentRoleInfo = roles.find(r => r.id === activeRole) || roles[0];
  const CurrentIcon = currentRoleInfo.icon;

  const handleRoleSwitch = (roleId: any) => {
    switchRole(roleId);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 hover:border-gray-300 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none cursor-pointer group"
        aria-label={`Switch active mode: current mode ${currentRoleInfo.label}`}
      >
        <div className={`p-1 rounded-md shrink-0 transition-colors ${
          activeRole === 'fpo_connect' ? 'bg-amber-50 text-amber-600' :
          'bg-emerald-50 text-emerald-600'
        }`}>
          <CurrentIcon className="w-4 h-4" />
        </div>
        <div className="text-left hidden xs:block">
          <span className="text-[10px] font-bold text-gray-400 block leading-none uppercase tracking-wider">Active Mode</span>
          <span className="text-xs font-extrabold text-gray-800 block mt-0.5 leading-none">{currentRoleInfo.label}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 origin-top-right rounded-xl bg-white border border-gray-100 shadow-xl ring-1 ring-black/5 z-[999] focus:outline-none overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="p-3 border-b border-gray-50 bg-gray-50/50">
            <h4 className="text-xs font-black text-gray-900 tracking-tight">Switch Context</h4>
            <p className="text-[10px] text-gray-500 mt-0.5">Change your active portal experience instantly.</p>
          </div>
          <div className="p-2 space-y-1">
            {roles.map((r) => {
              const IconComponent = r.icon;
              const isSelected = activeRole === r.id;
              
              return (
                <button
                  key={r.id}
                  onClick={() => handleRoleSwitch(r.id)}
                  className={`w-full flex items-start gap-3 p-2.5 rounded-lg transition-all text-left cursor-pointer ${
                    isSelected 
                      ? 'bg-zinc-900 text-white' 
                      : 'hover:bg-gray-55/40 text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <div className={`p-1.5 rounded-md shrink-0 ${
                    isSelected ? 'bg-white/10 text-white' : r.colorClass
                  }`}>
                    <IconComponent className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-extrabold block leading-tight">{r.label}</span>
                      {isSelected && (
                        <Check className="w-3.5 h-3.5 text-white shrink-0" />
                      )}
                    </div>
                    <span className={`text-[10px] block mt-0.5 ${
                      isSelected ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      {r.description}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
